import { useState, useCallback } from 'react';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '../../../Authentication/components/hooks/useUserOrg';

export interface QrAutomationInterface {
    id?: number;
    activationId: number;
    galleryId: number;
    title?: string;
    submitButtonText: string;
    confirmationText: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface QrAutomationDataField {
    id?: string;
    activationId: number;
    galleryId: number;
    type: 'boolean' | 'text' | 'email' | 'phoneNumber';
    name: string;
    placeholder?: string;
    orderIndex: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface QrAutomationSettings {
    interface: QrAutomationInterface | null;
    dataFields: QrAutomationDataField[];
}

export interface QrAutomationInstance {
    id: string;
    activationId: number;
    galleryId: number;
    redemptionCode: string;
    claimedAt?: string;
    invalidatedAt?: string;
    completedAt?: string;
    collectedData?: Record<string, any>;
    createdAt: string;
    instructions?: any[];
}

export default function useQrAutomation() {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [isLoading, setIsLoading] = useState(false);

    const getAutomationSettings = useCallback(
        async (activationId: number): Promise<QrAutomationSettings> => {
            setIsLoading(true);
            try {
                const response = await authenticatedApi.get(
                    `${orgId}/qr-automation/activation/${activationId}/settings`,
                );
                return response.data;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi, orgId],
    );

    const updateAutomationSettings = useCallback(
        async (
            activationId: number,
            settings: {
                interface?: Partial<QrAutomationInterface>;
                dataFields?: Omit<
                    QrAutomationDataField,
                    'id' | 'activationId' | 'createdAt' | 'updatedAt'
                >[];
            },
        ): Promise<QrAutomationSettings> => {
            setIsLoading(true);
            try {
                const response = await authenticatedApi.put(
                    `${orgId}/qr-automation/activation/${activationId}/settings`,
                    settings,
                );
                return response.data;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi, orgId],
    );

    const createAutomationInstance = useCallback(
        async (
            activationId: number,
            galleryId: number,
        ): Promise<QrAutomationInstance> => {
            setIsLoading(true);
            try {
                const response = await authenticatedApi.post(
                    `${orgId}/qr-automation/activation/${activationId}/instance`,
                    { galleryId },
                );
                return response.data;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi, orgId],
    );

    const getAutomationInstance = useCallback(
        async (instanceId: string): Promise<QrAutomationInstance> => {
            setIsLoading(true);
            try {
                const response = await authenticatedApi.get(
                    `${orgId}/qr-automation/instance/${instanceId}`,
                );
                return response.data;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi, orgId],
    );

    const invalidateAutomationInstance = useCallback(
        async (instanceId: string): Promise<QrAutomationInstance> => {
            setIsLoading(true);
            try {
                const response = await authenticatedApi.put(
                    `${orgId}/qr-automation/instance/${instanceId}/invalidate`,
                );
                return response.data;
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedApi, orgId],
    );

    return {
        isLoading,
        getAutomationSettings,
        updateAutomationSettings,
        createAutomationInstance,
        getAutomationInstance,
        invalidateAutomationInstance,
    };
}
